import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";

import Paper from "@mui/material/Paper";

import FormControlLabel from "@/components/atoms/form-label/label";
import Button from "@/components/atoms/button/button";
import styles from "./summary.module.scss";
import { hidePrefillQueryString } from "@/helpers/prefill-hook";
import { goToPage } from "@/helpers/stepHelper";
import { client } from "@/helpers/logger";
import { Fragment } from "react/jsx-runtime";
import classNames from "classnames";

type Props = {
  setActiveStep: (step: number) => void;
  activeStep: number;
  brand: string | string[] | undefined;
  data?: string | string[];
};
type FormElements = HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement;

function RenderValue(props: Readonly<{ value: string }>) {
  const parts = props.value.split(/\n/);
  return (
    <span>
      {parts.map((line, index) => (
        <Fragment key={`${line}-${index}`}>
          {line}
          <br />
        </Fragment>
      ))}
    </span>
  );
}

function SummaryStep(props: Readonly<Props>) {
  const { t } = useTranslation();
  const router = useRouter();
  const { setActiveStep, activeStep, data } = props;

  const form = document.querySelector("form");
  const formElements = Array.from(
    (form as HTMLFormElement).elements,
  ) as Array<FormElements>;

  const isDevelopment =
    process.env.NODE_ENV === "development" ||
    (globalThis.window !== undefined &&
      globalThis.location.href.includes("localhost"));
  if (!isDevelopment) {
    hidePrefillQueryString(data, router);
  }

  const getElements = (section: string): Array<FormElements> => {
    let elements = formElements.filter((el) => el.name.includes(section));
    const result: Array<FormElements> = [];
    elements.forEach((el) => {
      const textValue = getTextualValue(el);
      if (!el.value || !textValue || el.value === "YYYY-MM-DD") {
        return;
      }
      result.push(el);
    });
    return result;
  };
  const getTextLabel = (el: FormElements) => {
    let result = "";
    if (!el.labels) {
      return result;
    }
    const origLabel = el.labels[0];

    // remove input elements text if any
    result = origLabel.textContent?.replace(el.textContent ?? "", "") ?? "";

    // remove helper text
    result = result.replace(
      origLabel.querySelector(".MuiFormHelperText-root")?.textContent?.trim() ||
        "",
      "",
    );

    // remove upload input button text
    result = result.replace(
      origLabel.querySelector(".MuiButton-root")?.textContent?.trim() || "",
      "",
    );
    return result;
  };
  const getTextualValue = (el: FormElements) => {
    if (el.nodeName === "SELECT") {
      const select = el as HTMLSelectElement;
      return select.options[select.selectedIndex].textContent;
    }
    // gets the filename part only
    if (el.nodeName === "INPUT" && el.type === "file") {
      let result = "";
      Array.from((el as HTMLInputElement).files as FileList).forEach(
        (file: any) => {
          result += `${file.name}, `;
        },
      );
      result = result.replace(/, +$/, "");
      return result;
    }
    return el.value;
  };
  const objectifyTransactions = (
    els: Array<FormElements>,
  ): {
    [key: string]: Array<FormElements>;
  } => {
    let result: {
      [key: string]: Array<FormElements>;
    } = {};
    els.forEach((el: FormElements) => {
      if (result[el.name] === undefined) {
        result[el.name] = [el];
      } else {
        result[el.name] = [...result[el.name], el];
      }
    });
    return result;
  };

  const whatHappened = getElements("what-happened");
  const transactions = objectifyTransactions(
    getElements("transaction-details"),
  );
  const transactionFiles = getElements("transaction-details[attached-files]");
  const customer = getElements("customer");

  if (isDevelopment) {
    const getDirectLink = (opt: { development: boolean }) => {
      let skipFields = [
        // 'email-subject',
        "mailto",
        "files",
        "locale",
        "csrf_token",
      ];
      const skipFieldsProd = [
        "what-happened[attached-files]", // too big + might contain customer info which is not ok
        "what-happened[describe-what-happened]", // no identifiable customer info (a transaction without person is ok)
        "what-happened[attach-communication]",
        "what-happened[describe-the-incident-in-detail]",
        "what-happened[what-did-the-message-say]",
        "customer-information[contract-number]", // no identifiable customer info (a transaction without person is ok)
        "customer-information[account-number]",
        "customer-information[invoice-number]",
        "customer-information[email]",
        "customer-information[first-name]",
        "customer-information[last-name]",
        "customer__number-type",
        "customer-information[phone]",
      ];
      if (!opt.development) {
        skipFields = [...skipFields, ...skipFieldsProd];
      }
      let result = {};
      const form = document.querySelector("form") as HTMLFormElement;
      Array.from(form.elements).forEach((el: any) => {
        if (el.name && el.value && !skipFields.includes(el.name)) {
          result = {
            ...result,
            [el.name]: el.value,
          };
        }
      });
      return result;
    };
    const directLinkProd = getDirectLink({ development: false });
    const directLinkLocal = getDirectLink({ development: true });
    client.log(
      "direct link data Localhost (testing purposes only):",
      globalThis.btoa(JSON.stringify(directLinkLocal)),
    );
    client.log(
      "direct link app usage:",
      `window.btoa(JSON.stringify(data))`,
      directLinkProd,
    );
    client.log(
      "direct link data Prod:",
      globalThis.btoa(JSON.stringify(directLinkProd)),
    );
  }

  const firstName = customer.find(
    (field) => field.name === "customer-information[first-name]",
  )?.value;
  const lastName = customer.find(
    (field) => field.name === "customer-information[last-name]",
  )?.value;

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Card variant="outlined">
        <Paper>
          <CardContent>
            <Typography component="h3" variant="h6">
              {t("What happened")}
            </Typography>
            <Stack
              sx={{
                gap: 3,
              }}
            >
              {whatHappened.map((el) => {
                const heading = getTextLabel(el);
                const text = getTextualValue(el);
                const textValue = getTextualValue(el);
                if (!heading || !text || !textValue) {
                  return null;
                }
                return (
                  <Stack key={el.name}>
                    <Typography variant="body2">{getTextLabel(el)}</Typography>
                    <Typography variant="body1">
                      <RenderValue value={textValue} />
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>

          <Divider />

          <CardActions>
            <Button
              className={styles.btnSummaryEdit}
              onClick={() => {
                goToPage(0, setActiveStep);
              }}
              startIcon={<EditIcon />}
            >
              <span className="sr-only">
                {t("Change")} {t("What happened")}
              </span>
              <span aria-hidden="true">{t("Change")}</span>
            </Button>
          </CardActions>
        </Paper>
      </Card>
      <Card variant="outlined">
        <Paper>
          <CardContent>
            <Stack
              sx={{
                gap: 1,
              }}
            >
              <Typography component="h3" variant="h6">
                {t("Transaction details")}
              </Typography>
              <div>
                {transactions["transaction-details[merchant]"]?.map(
                  (el, index) => {
                    return (
                      <Accordion key={el.value}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          className={classNames([styles.accordionSummary])}
                        >
                          <Typography
                            sx={{ gap: 1, display: "flex" }}
                            component="p"
                          >
                            <Typography variant="subtitle2" component="span">
                              {
                                transactions[
                                  "transaction-details[purchase-date]"
                                ][index].value
                              }
                            </Typography>
                            <Typography variant="body1" component="span">
                              {el.value}
                            </Typography>
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack
                            sx={{
                              gap: 3,
                            }}
                          >
                            <Stack>
                              <Typography variant="body2">
                                {getTextLabel(
                                  transactions[
                                    "transaction-details[select-currency]"
                                  ][index],
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {getTextualValue(
                                  transactions[
                                    "transaction-details[select-currency]"
                                  ][index],
                                )}
                              </Typography>
                            </Stack>
                            <Stack>
                              <Typography variant="body2">
                                {getTextLabel(
                                  transactions["transaction-details[amount]"][
                                    index
                                  ],
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {getTextualValue(
                                  transactions["transaction-details[amount]"][
                                    index
                                  ],
                                )}
                              </Typography>
                            </Stack>
                            <Stack>
                              <Typography variant="body2">
                                {getTextLabel(
                                  transactions["transaction-details[merchant]"][
                                    index
                                  ],
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {getTextualValue(
                                  transactions["transaction-details[merchant]"][
                                    index
                                  ],
                                )}
                              </Typography>
                            </Stack>
                            <Stack>
                              <Typography variant="body2">
                                {getTextLabel(
                                  transactions[
                                    "transaction-details[purchase-date]"
                                  ][index],
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {getTextualValue(
                                  transactions[
                                    "transaction-details[purchase-date]"
                                  ][index],
                                )}
                              </Typography>
                            </Stack>
                            <Button
                              className={styles.btnSummaryEdit}
                              onClick={() => {
                                goToPage(1, setActiveStep);
                              }}
                              startIcon={<EditIcon />}
                            >
                              <span className="sr-only">
                                {t("Change")} {t("My contact details")}
                              </span>
                              <span aria-hidden="true">{t("Change")}</span>
                            </Button>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  },
                )}
                {transactionFiles.length ? (
                  <div style={{ marginTop: "1rem" }}>
                    {transactionFiles.map((el) => {
                      const textLabel = getTextLabel(el);
                      const textValue = getTextualValue(el);
                      return (
                        <Stack key={`${textLabel}_${textValue}`}>
                          <Typography variant="body2">{textLabel}</Typography>
                          <Typography variant="body1">{textValue}</Typography>
                        </Stack>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </Stack>
          </CardContent>

          <Divider />

          <CardActions>
            <Button
              className={styles.btnSummaryEdit}
              onClick={() => {
                goToPage(1, setActiveStep);
              }}
              startIcon={<EditIcon />}
            >
              <span className="sr-only">
                {t("Change")} {t("Transaction details")}
              </span>
              <span aria-hidden="true">{t("Change")}</span>
            </Button>
          </CardActions>
        </Paper>
      </Card>
      <Card variant="outlined">
        <Paper>
          <CardContent>
            <Typography component="h3" variant="h6">
              {t("My contact details")}
            </Typography>
            <Stack
              sx={{
                gap: 3,
              }}
            >
              {customer.map((el) => {
                const textLabel = getTextLabel(el);
                const textValue = getTextualValue(el);
                return (
                  <Stack key={`${textLabel}_${textValue}`}>
                    <Typography variant="body2">{textLabel}</Typography>
                    <Typography variant="body1">{textValue}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions>
            <Button
              className={styles.btnSummaryEdit}
              onClick={() => {
                goToPage(2, setActiveStep);
              }}
              startIcon={<EditIcon />}
            >
              <span className="sr-only">
                {t("Change")} {t("My contact details")}
              </span>
              <span aria-hidden="true">{t("Change")}</span>
            </Button>
          </CardActions>
        </Paper>
      </Card>
      {props.brand === "sebdebit" ? null : (
        <Card variant="outlined">
          <Paper>
            <CardContent>
              <FormControl className={activeStep === 3 ? "required" : ""}>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  className={styles.whatWillYouDo}
                >
                  {t(
                    "What would you like to do with the payment of the billed transactions(s) while waiting for the dispute to be investigated",
                  )}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="female"
                  name="radio-buttons-group"
                >
                  <FormControlLabel
                    value="I will pay for the disputed transaction(s) with my next payment."
                    control={
                      <Radio
                        required={activeStep === 3}
                        inputProps={{
                          name: "summary[skip-label-what-to-do-with-payment]",
                        }}
                      />
                    }
                    label={t(
                      "I will pay for the disputed transaction(s) with my next payment.",
                    )}
                  />
                  <FormControlLabel
                    value="I will not pay for the disputed transaction (s) with my next payment."
                    control={
                      <Radio
                        required={activeStep === 3}
                        inputProps={{
                          name: "summary[skip-label-what-to-do-with-payment]",
                        }}
                      />
                    }
                    label={t(
                      "I will not pay for the disputed transaction (s) with my next payment.",
                    )}
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Paper>
        </Card>
      )}

      <Card variant="outlined">
        <Paper>
          <CardContent>
            <FormGroup>
              <FormControlLabel
                hasCheckOrRadiobox
                className={styles.certify}
                control={
                  <Checkbox
                    required={activeStep === 3}
                    name="summary[i-hereby-certify-that-the-given-information-is-true-and-correct]"
                  />
                }
                label={t(
                  "I hereby certify that the given information is true and correct",
                )}
              />
            </FormGroup>
          </CardContent>
        </Paper>
      </Card>
      <Card variant="outlined">
        <Paper>
          <CardContent>
            <FormGroup>
              <FormControlLabel
                hasCheckOrRadiobox
                className={styles.certify}
                control={<Checkbox name="summary[send-copy]" />}
                label={t(
                  "I wish to recieve a copy of this form to my email (Optional)",
                )}
              />
            </FormGroup>
          </CardContent>
        </Paper>
      </Card>
      <input
        type="hidden"
        readOnly
        name="summary[regards]"
        value={`${firstName} ${lastName}`}
      />
      {/* needed for the email */}
    </Stack>
  );
}

export default SummaryStep;
